import ArrSVG from '@public/icons/btn-arr.svg';
import CloseSVG from '@public/icons/close-btn.svg';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { AnimatePresence, m as motion } from 'framer-motion';
import { FocusEventHandler, useRef, useState } from 'react';
import { object, string } from 'yup';
import { postSubscribe } from '@/shared/api/subscribe';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import Button from '@/shared/components/shared/Button';
import Input from '@/shared/components/shared/Input';
import Popup from '@/shared/components/shared/Popup';
import Typografed from '@/shared/components/shared/Typografed';
import { FeedbackResponse } from '@/shared/lib/types';

export const SUBSCRIBE_POPUP_NAME = 'subscribe-popup';

function SubscribePopup({ data = {} }) {
    const timeoutRef = useRef<NodeJS.Timeout>();
    const [response, setResponse] = useState<FeedbackResponse | null>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const { closePopup } = useSetOpenedPopupsState();

    const schema = object({
        email: string()
            .required('Поле обязательно к заполнению')
            .matches(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/, 'Введите корректный адрес эл. почты'),
    });

    const {
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        errors,
        touched,
        isSubmitting,
        setFieldTouched,
        setValues,
        setTouched,
        initialTouched,
        initialValues,
    } = useFormik({
        initialValues: {
            ...data,
            check_val: '1',
            email: '',
        },
        validationSchema: schema,
        onSubmit: async (values) => {
            clearTimeout(timeoutRef.current);
            setResponse(null);

            try {
                const data = await postSubscribe(values);
                setResponse({
                    status: data.status,
                    message: data.data?.message || 'Успешно отправлено',
                });

                if (data.status === 'success') {
                    setValues(initialValues);
                    setTouched(initialTouched);
                } else {
                    setResponse({ status: 'error', message: data.data?.message || 'Что-то пошло не так' });
                }
            } catch (err) {
                setResponse({ status: 'error', message: 'Что-то пошло не так' });

                throw err;
            } finally {
                timeoutRef.current = setTimeout(() => {
                    setResponse(null);
                }, 5000);
            }
        },
    });

    const onFocus: FocusEventHandler<HTMLInputElement> = (event) => {
        setFieldTouched(event.target.name, false);
    };

    return (
        <Popup name={SUBSCRIBE_POPUP_NAME} data-popup-preset="slide-right" className="subscribe-popup" overlay={true}>
            <button
                className="menu-popup-close-btn"
                onClick={() => {
                    closePopup(SUBSCRIBE_POPUP_NAME);
                }}
            >
                <CloseSVG />
            </button>
            <div className="subscribe-popup-text text-xl">
                <Typografed>Оставайтесь в курсе актуальных новостей форума</Typografed>
            </div>
            <form ref={formRef} className="subscribe-popup-input-wrapper" noValidate onSubmit={handleSubmit}>
                <Input
                    type="email"
                    name="email"
                    className="subscribe-popup-input"
                    placeholder={'Введите свой e-mail'}
                    required
                    label=""
                    disabled={isSubmitting}
                    id="feedback-form-email"
                    value={values.email}
                    onInput={handleChange}
                    onChange={handleChange}
                    onFocus={onFocus}
                    onBlur={handleBlur}
                    message={touched.email ? errors.email : ''}
                    valid={!(!!touched.email && !!errors.email)}
                />
                <button
                    type="submit"
                    className={classNames('subscribe-popup-input-btn', { 'is-submitting': isSubmitting })}
                    disabled={isSubmitting}
                >
                    <ArrSVG />
                </button>
            </form>
            <AnimatePresence>
                {response?.message && (
                    <motion.div
                        className="subscribe-popup-input-response"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        {response.status === 'success' && (
                            <div className="subscribe-popup-response-title text-l">
                                <Typografed>Спасибо за подписку!</Typografed>
                            </div>
                        )}
                        <div className="subscribe-popup-response-description text-s">
                            <Typografed>{response.message}</Typografed>
                        </div>
                        <Button
                            variant="primary-filled"
                            onClick={() => {
                                closePopup(SUBSCRIBE_POPUP_NAME);
                            }}
                        >
                            Закрыть
                        </Button>
                    </motion.div>
                )}
            </AnimatePresence>
        </Popup>
    );
}

export default SubscribePopup;
